import { Show } from "@refinedev/antd";
import { CanAccess, useNotification, useOne } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Image,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { api } from "../api";
import { ReasonPicker } from "../components/reason-picker";
import {
  DATE_TIME_FORMAT,
  PROFILE_CHANGE_FIELDS,
  PROFILE_CHANGE_REJECT_REASONS,
  PROFILE_CHANGE_STATUS,
} from "../labels";
import type { ProfileChangeDetail, ProfileChangeField, ProfileChangeSide } from "../types";

/** What students see now, or would see after approval. */
function Side({ field, side }: { field: ProfileChangeField; side: ProfileChangeSide }) {
  if (field === "PHOTO") {
    return side.fileUrl ? <Image src={side.fileUrl} width={200} /> : <Empty description="Yok" />;
  }
  if (field === "VIDEO") {
    return side.fileUrl ? (
      <video src={side.fileUrl} controls style={{ width: "100%", maxHeight: 320 }} />
    ) : (
      <Empty description="Yok" />
    );
  }
  if (field === "SUBJECTS") {
    return (
      <Space wrap>
        {(side.list ?? []).map((name) => (
          <Tag key={name}>{name}</Tag>
        ))}
      </Space>
    );
  }
  return (
    <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
      {side.text || "—"}
    </Typography.Paragraph>
  );
}

/**
 * /profile-changes/:id: the current value next to the change, then approve or reject with a
 * reason the tutor sees (product decision 2026-10-13). File links last a few minutes.
 */
export function ProfileChangeShowPage() {
  const { id = "" } = useParams();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const { result: change, query } = useOne<ProfileChangeDetail>({
    resource: "profile-changes",
    id,
  });
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm<{ note: string }>();

  async function decide(body: { decision: "APPROVE" } | { decision: "REJECT"; note: string }) {
    setBusy(true);
    try {
      await api(`/api/office/profile-changes/${encodeURIComponent(id)}/decision`, {
        method: "POST",
        body,
      });
      open?.({
        type: "success",
        message: body.decision === "APPROVE" ? "Değişiklik onaylandı." : "Değişiklik reddedildi.",
      });
      setRejecting(false);
      form.resetFields();
      await Promise.all([
        query.refetch(),
        queryClient.invalidateQueries({ queryKey: ["office", "badges"] }),
      ]);
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
    } finally {
      setBusy(false);
    }
  }

  const label = change ? (PROFILE_CHANGE_FIELDS[change.field] ?? change.field) : "Değişiklik";
  return (
    <Show
      title={change ? `${change.tutor.name} · ${label}` : "Profil değişikliği"}
      isLoading={query.isLoading}
      canEdit={false}
      canDelete={false}
      headerButtons={
        change?.status === "PENDING" ? (
          <CanAccess resource="profile-changes" action="decide">
            <Space>
              <Button danger onClick={() => setRejecting(true)}>
                Reddet
              </Button>
              <Popconfirm
                title="Değişiklik onaylansın mı?"
                description="Hemen profilde öğrencilere görünür."
                okText="Onayla"
                cancelText="Vazgeç"
                onConfirm={() => decide({ decision: "APPROVE" })}
              >
                <Button type="primary" loading={busy}>
                  Onayla
                </Button>
              </Popconfirm>
            </Space>
          </CanAccess>
        ) : null
      }
    >
      {change ? (
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Eğitmen">
              <Link to={`/tutors/${change.tutor.id}`}>{change.tutor.name}</Link>
            </Descriptions.Item>
            <Descriptions.Item label="Gönderildi">
              {dayjs(change.createdAt).format(DATE_TIME_FORMAT)}
            </Descriptions.Item>
            <Descriptions.Item label="Durum">
              <Tag color={PROFILE_CHANGE_STATUS[change.status]?.color}>
                {PROFILE_CHANGE_STATUS[change.status]?.label}
              </Tag>
              {change.decidedAt ? dayjs(change.decidedAt).format(DATE_TIME_FORMAT) : null}
            </Descriptions.Item>
            {change.reviewNote ? (
              <Descriptions.Item label="Ret gerekçesi">{change.reviewNote}</Descriptions.Item>
            ) : null}
          </Descriptions>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card size="small" title="Şu an profilde">
                <Side field={change.field} side={change.before} />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card size="small" title="Yeni">
                <Side field={change.field} side={change.after} />
              </Card>
            </Col>
          </Row>
        </Space>
      ) : null}

      <Modal
        title="Değişikliği reddet"
        open={rejecting}
        okText="Reddet"
        okButtonProps={{ danger: true }}
        cancelText="Vazgeç"
        confirmLoading={busy}
        onOk={() => form.submit()}
        onCancel={() => setRejecting(false)}
      >
        <Typography.Paragraph type="secondary">
          Profilde şu anki hali kalır. Gerekçe eğitmene gösterilir ve e-postayla gider; eğitmen
          düzeltip tekrar gönderebilir.
        </Typography.Paragraph>
        <ReasonPicker
          reasons={change ? (PROFILE_CHANGE_REJECT_REASONS[change.field] ?? []) : []}
          onPick={(reason) => form.setFieldValue("note", reason)}
        />
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={({ note }) => decide({ decision: "REJECT", note })}
        >
          <Form.Item
            label="Gerekçe"
            name="note"
            rules={[
              {
                required: true,
                whitespace: true,
                min: 5,
                message: "Gerekçeyi yaz (en az 5 karakter).",
              },
              { max: 500, message: "Gerekçe en fazla 500 karakter olabilir." },
            ]}
          >
            <Input.TextArea rows={4} showCount maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </Show>
  );
}
